import React, { useEffect, useState } from 'react';

import { CourseResourceIcon } from '@/assets/ui/activity';
import UtilityService from '@/services/util/utility-service';
import { CopyIcon } from '@/assets/icons/button';

interface URLPreviewProps {
  url: string;
}

const URLPreview: React.FC<URLPreviewProps> = ({ url }) => {
  const [title, setTitle] = useState<string | null>(null);

  useEffect(() => {
    const fetchTitle = async () => {
      setTitle(null);
      try {
        // Not critical, but have to use a cors proxy to retrieve URL's title.
        // Currently free, has a rate limiter. Possibility of a self hosted solution later (or outright removing preview) if we run into issues.
        const response = await fetch(
          `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`,
        );
        const html = await response.text();

        // Parse meta tags
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const docTitle = doc.querySelector('title')?.innerText ?? undefined;
        const ogTitle =
          doc.querySelector('meta[property="og:title"]')?.getAttribute('content') ?? docTitle;
        const resolved = (ogTitle ?? '').trim();
        if (resolved) {
          setTitle(resolved);
        }
      } catch (error) {
        console.error('Error fetching meta tags:', error);
        // Do not set an error title; keep fallback link display
      }
    };

    fetchTitle();
  }, [url]);

  // Compute a safe origin/hostname for display while loading
  const safeOrigin = (() => {
    try {
      return new URL(url).origin;
    } catch {
      return url;
    }
  })();

  return (
    <div className='bg-surface_gray_50 rounded-xl my-4 border border-border_gray_200 drop-shadow-md p-4 flex flex-row items-center justify-between'>
      <div className='flex flex-row items-center justify-center gap-4'>
        {title && (
          <>
            <CourseResourceIcon className='w-5 h-5 text-icon_primary' />
            <a
              href={url}
              target='_blank'
              rel='noopener noreferrer'
              className='text-blue-500 text-lg hover:underline'
            >
              {title}
            </a>
          </>
        )}
        {!title && (
          <>
            <CourseResourceIcon className='w-5 h-5 text-icon_primary' />
            <a
              href={url}
              target='_blank'
              rel='noopener noreferrer'
              className='text-blue-500 text-lg hover:underline'
            >
              {`Go to ${safeOrigin}`}
            </a>
          </>
        )}
      </div>
      <div
        className='flex flex-row items-center justify-center gap-2 cursor-pointer'
        onClick={() => UtilityService.copyToClipboard(url)}
      >
        <CopyIcon className='w-6 h-6' />
        {'Copy Link'}
      </div>
    </div>
  );
};

export default URLPreview;
